import { atualizarRiscosVinculados } from './encaixeRiscos'
import type {
  AtualizacoesObjetoTabletop,
  CenaTabletop,
  GeradorIdTabletop,
  MeteorologiaTabletop,
  NovoObjetoTabletop,
  ObjetoTabletop,
  TipoObjetoTabletop,
  TransformacaoTabletop,
  VetorTabletop,
} from './tipos'

export const TRANSFORMACAO_TABLETOP_PADRAO: TransformacaoTabletop = Object.freeze({
  posicao: Object.freeze([0, 0, 0]) as VetorTabletop,
  rotacao: Object.freeze([0, 0, 0]) as VetorTabletop,
  escala: Object.freeze([1, 1, 1]) as VetorTabletop,
})

export const CENA_TABLETOP_VAZIA: CenaTabletop = Object.freeze({
  versao: 1,
  nome: 'Novo table top',
  objetos: Object.freeze([]),
})

const NOMES_PADRAO: Readonly<Record<TipoObjetoTabletop, string>> = Object.freeze({
  caminhaoTanque: 'Caminhão-tanque',
  carro: 'Carro de passeio',
  viaturaAbt: 'Viatura ABT',
  viaturaHazmat: 'Viatura HAZMAT',
  ambulancia: 'Resgate dos bombeiros',
  bombeiroNivelA: 'Bombeiro (EPI nível A)',
  bombeiroNivelB: 'Bombeiro (EPI nível B)',
  bombeiroNivelC: 'Bombeiro (EPI nível C)',
  bombeiroIncendio: 'Bombeiro (roupão de incêndio)',
  medico: 'Médico / resgatista APH',
  vazamento: 'Vazamento',
  liquidoDerramado: 'Líquido derramado',
  fogo: 'Fogo',
  fogoTanque: 'Fogo saindo do tanque',
  bleve: 'BLEVE (efeito visual)',
  curtoCircuito: 'Curto-circuito',
  predio: 'Prédio',
  escola: 'Escola',
  hospital: 'Hospital',
  corrego: 'Córrego',
  bueiro: 'Bueiro',
  cone: 'Cone',
  fita: 'Fita de isolamento',
  postoComando: 'Posto de comando',
  areaVitimas: 'Área de concentração de vítimas',
  corredorDescontaminacao: 'Corredor de descontaminação',
  biruta: 'Biruta',
  vitima: 'Vítima',
})

const copiarVetor = (vetor: VetorTabletop): VetorTabletop => [vetor[0], vetor[1], vetor[2]]

export const copiarTransformacao = (
  transformacao: TransformacaoTabletop,
): TransformacaoTabletop => ({
  posicao: copiarVetor(transformacao.posicao),
  rotacao: copiarVetor(transformacao.rotacao),
  escala: copiarVetor(transformacao.escala),
})

export const copiarObjetoTabletop = (objeto: ObjetoTabletop): ObjetoTabletop => ({
  id: objeto.id,
  tipo: objeto.tipo,
  nome: objeto.nome,
  transformacao: copiarTransformacao(objeto.transformacao),
  ...(objeto.cor === undefined ? {} : { cor: objeto.cor }),
  ...(objeto.bloqueado === undefined ? {} : { bloqueado: objeto.bloqueado }),
  ...(objeto.oculto === undefined ? {} : { oculto: objeto.oculto }),
  ...(objeto.idTanque === undefined ? {} : { idTanque: objeto.idTanque }),
  ...(objeto.fonte === undefined ? {} : { fonte: { ...objeto.fonte } }),
})

export const copiarCenaTabletop = (cena: CenaTabletop): CenaTabletop => ({
  versao: 1,
  nome: cena.nome,
  ...(cena.historiaInicial === undefined ? {} : { historiaInicial: cena.historiaInicial }),
  objetos: cena.objetos.map(copiarObjetoTabletop),
  ...(cena.acionamento
    ? {
        acionamento: {
          ...cena.acionamento,
          informacoes: cena.acionamento.informacoes.map((item) => ({ ...item })),
        },
      }
    : {}),
  ...(cena.meteorologia ? { meteorologia: { ...cena.meteorologia } } : {}),
})

export const METEOROLOGIA_PADRAO: MeteorologiaTabletop = Object.freeze({
  direcaoVentoGraus: 90,
  ventoMS: 3,
  periodo: 'dia',
  ceuEncoberto: false,
  terreno: 'aberto',
})

export const meteorologiaDaCena = (cena: CenaTabletop): MeteorologiaTabletop => cena.meteorologia ?? METEOROLOGIA_PADRAO

export function definirMeteorologia(cena: CenaTabletop, alteracoes: Partial<MeteorologiaTabletop>): CenaTabletop {
  return { ...cena, meteorologia: { ...meteorologiaDaCena(cena), ...alteracoes } }
}

export function criarCenaVazia(nome = CENA_TABLETOP_VAZIA.nome): CenaTabletop {
  return { versao: 1, nome, objetos: [] }
}

const gerarIdComEntropiaNativa = (): string | undefined => {
  const criptografia = globalThis.crypto
  if (!criptografia) return undefined

  if (typeof criptografia.randomUUID === 'function') {
    return criptografia.randomUUID()
  }

  if (typeof criptografia.getRandomValues === 'function') {
    const valores = criptografia.getRandomValues(new Uint32Array(4))
    return `tabletop-${Array.from(valores, (valor) =>
      valor.toString(16).padStart(8, '0'),
    ).join('')}`
  }

  return undefined
}

const gerarIdPadrao: GeradorIdTabletop = () =>
  gerarIdComEntropiaNativa() ??
  `tabletop-${Date.now().toString(36)}-${Math.floor(Math.random() * 0x1_0000_0000)
    .toString(36)
    .padStart(7, '0')}`

const tornarIdUnico = (cena: CenaTabletop, idBase: string): string => {
  if (!cena.objetos.some((objeto) => objeto.id === idBase)) return idBase

  let sufixo = 2
  while (cena.objetos.some((objeto) => objeto.id === `${idBase}-${sufixo}`)) {
    sufixo += 1
  }
  return `${idBase}-${sufixo}`
}

const validarNovoId = (cena: CenaTabletop, id: string): void => {
  if (id.trim().length === 0) throw new Error('Não foi possível gerar um ID válido para o objeto.')
  if (cena.objetos.some((objeto) => objeto.id === id)) {
    throw new Error(`Já existe um objeto com o ID "${id}".`)
  }
}

export function adicionarObjeto(
  cena: CenaTabletop,
  novoObjeto: NovoObjetoTabletop,
  gerarId?: GeradorIdTabletop,
): CenaTabletop {
  const idGerado = (gerarId ?? gerarIdPadrao)()
  const id = gerarId === undefined ? tornarIdUnico(cena, idGerado) : idGerado
  validarNovoId(cena, id)
  const objeto: ObjetoTabletop = {
    id,
    tipo: novoObjeto.tipo,
    nome: novoObjeto.nome ?? NOMES_PADRAO[novoObjeto.tipo],
    transformacao: copiarTransformacao(
      novoObjeto.transformacao ?? TRANSFORMACAO_TABLETOP_PADRAO,
    ),
    ...(novoObjeto.cor === undefined ? {} : { cor: novoObjeto.cor }),
    ...(novoObjeto.idTanque === undefined ? {} : { idTanque: novoObjeto.idTanque }),
    ...(novoObjeto.fonte === undefined ? {} : { fonte: { ...novoObjeto.fonte } }),
  }

  return { ...cena, objetos: [...cena.objetos, objeto] }
}

export function atualizarTransformacao(
  cena: CenaTabletop,
  id: string,
  alteracoes: Partial<TransformacaoTabletop>,
): CenaTabletop {
  return atualizarObjeto(cena, id, { transformacao: alteracoes })
}

export function atualizarObjeto(
  cena: CenaTabletop,
  id: string,
  alteracoes: AtualizacoesObjetoTabletop,
): CenaTabletop {
  if (!cena.objetos.some((objeto) => objeto.id === id)) return cena

  const proxima: CenaTabletop = {
    ...cena,
    objetos: cena.objetos.map((objeto) => {
      if (objeto.id !== id) return objeto
      const transformacao = alteracoes.transformacao
        ? {
            posicao: copiarVetor(
              alteracoes.transformacao.posicao ?? objeto.transformacao.posicao,
            ),
            rotacao: copiarVetor(
              alteracoes.transformacao.rotacao ?? objeto.transformacao.rotacao,
            ),
            escala: copiarVetor(alteracoes.transformacao.escala ?? objeto.transformacao.escala),
          }
        : objeto.transformacao

      return {
        ...objeto,
        ...(alteracoes.idTanque === undefined ? {} : { idTanque: alteracoes.idTanque ?? undefined }),
        ...(alteracoes.nome === undefined ? {} : { nome: alteracoes.nome }),
        ...(alteracoes.cor === undefined ? {} : { cor: alteracoes.cor }),
        ...(alteracoes.bloqueado === undefined ? {} : { bloqueado: alteracoes.bloqueado }),
        ...(alteracoes.oculto === undefined ? {} : { oculto: alteracoes.oculto }),
        transformacao,
        ...(alteracoes.fonte === undefined
          ? {}
          : { fonte: { idProduto: '', enchimento: 0, ...objeto.fonte, ...alteracoes.fonte } }),
      }
    }),
  }
  return alteracoes.transformacao || alteracoes.fonte ? atualizarRiscosVinculados(proxima, id) : proxima
}

export function duplicarObjeto(
  cena: CenaTabletop,
  id: string,
  gerarId?: GeradorIdTabletop,
): CenaTabletop {
  const original = cena.objetos.find((objeto) => objeto.id === id)
  if (!original) throw new Error(`Não foi encontrado um objeto com o ID "${id}".`)

  return adicionarObjeto(
    cena,
    {
      tipo: original.tipo,
      nome: `${original.nome} (cópia)`,
      transformacao: original.transformacao,
      ...(original.cor === undefined ? {} : { cor: original.cor }),
      ...(original.idTanque === undefined ? {} : { idTanque: original.idTanque }),
      ...(original.fonte === undefined ? {} : { fonte: original.fonte }),
    },
    gerarId,
  )
}

export function removerObjeto(cena: CenaTabletop, id: string): CenaTabletop {
  if (!cena.objetos.some((objeto) => objeto.id === id)) return cena
  return { ...cena, objetos: cena.objetos.filter((objeto) => objeto.id !== id).map(objeto => {
    if (objeto.idTanque !== id) return objeto
    const { idTanque, ...livre } = objeto
    void idTanque
    return livre
  }) }
}

export function renomearCena(cena: CenaTabletop, nome: string): CenaTabletop {
  return { ...cena, nome }
}

/**
 * Encaixa um ponto do chão numa grade quadrada (padrão 0,25 m): a peça
 * arrastada ou tocada pousa em posições redondas, sem exigir precisão de
 * milímetro no toque. Passe `passoM` maior/menor para grades mais grossas
 * ou finas; `null`/`0` desliga o encaixe e devolve o ponto como veio.
 */
export function encaixarPosicaoXZ(
  x: number,
  z: number,
  passoM: number | null = 0.25,
): readonly [number, number] {
  if (!passoM) return [x, z]
  return [Math.round(x / passoM) * passoM, Math.round(z / passoM) * passoM]
}

/**
 * Mesma ideia do encaixe de posição, mas para o giro em Y: prende o ângulo
 * nos múltiplos de `passoGraus` (padrão 15°) para girar uma peça no toque
 * parar em ângulos redondos em vez de fracionários.
 */
export function encaixarRotacaoGraus(graus: number, passoGraus: number | null = 15): number {
  if (!passoGraus) return graus
  return Math.round(graus / passoGraus) * passoGraus
}

